import { forwardRef, memo, type HTMLAttributes } from 'react';
import { cva, type CVAVariantProps } from '../../utils/variants';
import { cn } from '../../utils/cn';
import type { BaseComponentProps } from '../../types';

/**
 * Varianti del Badge
 * AGGIORNATO: Usa variabili tema con opacità per badge style
 */
const badgeVariants = cva(
  'inline-flex items-center justify-center font-medium rounded-full transition-colors',
  {
    variants: {
      variant: {
        primary: 'bg-primary/10 text-primary border border-primary/20',
        secondary: 'bg-secondary/10 text-secondary border border-secondary/20',
        success: 'bg-success/10 text-success border border-success/20',
        error: 'bg-error/10 text-error border border-error/20',
        warning: 'bg-warning/10 text-warning border border-warning/20',
        info: 'bg-info/10 text-info border border-info/20',
        ghost: 'bg-neutral/10 text-neutral border border-neutral/20',
        link: 'bg-neutral/10 text-neutral border border-neutral/20',
      },
      size: {
        xs: 'text-xs px-1.5 py-0.5',
        sm: 'text-xs px-2 py-1',
        md: 'text-sm px-2.5 py-1',
        lg: 'text-base px-3 py-1.5',
        xl: 'text-lg px-4 py-2',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    BaseComponentProps,
    CVAVariantProps<typeof badgeVariants> {
  /**
   * Mostra un dot indicator
   */
  dot?: boolean;
  /**
   * Posizione del dot
   */
  dotPosition?: 'left' | 'right';
  /**
   * Callback quando il badge viene chiuso (rende il badge dismissible)
   */
  onDismiss?: () => void;
}

/**
 * Componente Badge
 * 
 * Etichetta piccola per stati, categorie, contatori, etc.
 * Supporta dot indicator e opzione dismissible.
 * Ottimizzato con React.memo per prevenire re-render non necessari.
 * 
 * @example
 * ```tsx
 * <Badge variant="success">Active</Badge>
 * <Badge variant="error" dot>Error</Badge>
 * <Badge variant="info" onDismiss={() => {}}>Dismissible</Badge>
 * ```
 */
const BadgeComponent = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      variant,
      size,
      dot,
      dotPosition = 'left',
      onDismiss,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant, size }), className)}
        {...props}
      >
        {dot && dotPosition === 'left' && (
          <span
            className="w-1.5 h-1.5 rounded-full bg-current mr-1.5"
            aria-hidden="true"
          />
        )}
        
        {children}
        
        {dot && dotPosition === 'right' && (
          <span
            className="w-1.5 h-1.5 rounded-full bg-current ml-1.5"
            aria-hidden="true"
          />
        )}
        
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            className="ml-1.5 hover:opacity-70 focus:outline-none focus:ring-1 focus:ring-current rounded"
            aria-label="Rimuovi badge"
          >
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </span>
    );
  }
);

BadgeComponent.displayName = 'Badge';

export const Badge = memo(BadgeComponent);

